import axios from 'axios';
import uniq from 'lodash/uniq';
import set from 'lodash/set';
import groupBy from 'lodash/groupBy';
import some from 'lodash/some';

import { API, CUSTOMERGATEWAY_BASE_URL } from '@constants';
import { retrieveAccessToken, removeAccessToken } from '@utils';

const clients = {
  portal: axios.create({}), // TODO: Set the Gatsby URL here instead of repeating in constants.
  gateway: axios.create({ baseURL: CUSTOMERGATEWAY_BASE_URL }),
};

// Have the client logout in the event of a 401 (assuming that's how the server responds in the event of inadequate credentials)
Object.keys(clients).forEach(client => {
  clients[client].interceptors.response.use(
    response => response,
    error => {
      const responseStatus = error?.response?.status;
      const requestURL = error?.config?.url;

      // These endpoints respond 404 if a dataset is empty
      const is404 = responseStatus === 404;
      const isVehicles = requestURL.includes('/vehicles');
      const isDocuments = requestURL.includes('/document-info');
      const isEmptyDataResponse = is404 && (isVehicles || isDocuments);
      if (isEmptyDataResponse) {
        return new Promise(resolve => resolve());
      }
      if (!isEmptyDataResponse && responseStatus === 401) {
        removeAccessToken();
        window.location.href = '/commerce/logout';
      }
    }
  );
});

// TODO - Update function signatures to remove necessity for token.
export const getUserProfile = async (_token, resetCache) => {
  // Have axios instances apply bearer token in header moving forward.
  Object.keys(clients).forEach(clientKey => {
    clients[clientKey].interceptors.request.use(config => {
      const token = retrieveAccessToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });
  });

  const profileResponse = await clients['portal'].get(API.PROFILE, {
    params: { resetCache },
  });
  const hasDeliveredVehicles = some(
    (profileResponse?.data?.data?.orders || []).filter(
      order => order.status === 'FULFILLED'
    )
  );

  let vehicleResponse;
  let vehicles = [];
  let documents = [];

  // Only reach out to infotainment APIs if we have a delivered vehicle.
  if (hasDeliveredVehicles) {
    vehicleResponse = await clients['gateway'].get('/vehicles');
    vehicles = vehicleResponse?.data?.Vehicles || [];
    if (vehicles?.length) {
      // Fetch documents.
      const document_types = [
        'RELEASE_NOTES_PRE',
        'RELEASE_NOTES_POST',
        'OWNERS_MANUAL',
        'SERVICE_NOTICE',
        'RECALL_NOTICE',
      ];

      // For each software version, pull the appropriate documentation.
      const softwareVersions = uniq(
        vehicles.map(vehicle => vehicle.softwareVersion)
      );

      const requestURLs = [];
      document_types.forEach(docType => {
        softwareVersions.forEach(version => {
          requestURLs.push(
            `/document-info?version=${version}&documentType=${docType}`
          );
        });
      });

      documents = (
        await Promise.all(requestURLs.map(url => clients['gateway'].get(url)))
      )
        .filter(doc => !!doc) // Get rid of 404s.
        .reduce((acc, currVal) => {
          const version = currVal.config.url.match(/version=([^&]+)/)?.[1]; // Pull the version from the request url.
          if (!acc[version]) {
            acc[version] = [];
          }
          acc[version].push(currVal.data);
          return acc;
        }, {});

      set(
        profileResponse,
        'data.data.infotainmentVehicles',
        groupBy(vehicles, 'vin')
      );
    }
  }

  if (profileResponse) {
    return { status: 200, data: profileResponse.data, vehicles, documents }; // Keeping profile data as 'data' to satisfy existing contracts.
  }
};

// This one is for profile updates.
export const updateUserProfile = async data =>
  clients['portal'].post(API.PROFILE, data);

// This one is for vehicle updates.
export const putUserProfile = async (_token, data) =>
  clients['portal'].put(API.PROFILE, data);
export const updateUserReservation = async (_token, data) =>
  clients['portal'].put(API.USER_RESERVE, data);
export const upgradeUserReservation = async (_token, data) =>
  clients['portal'].put(API.RESERVATION_UPGRADE, data);
export const updateUserReservationWaitlist = async (_token, data) =>
  clients['portal'].put(API.RESERVATION_WAITLIST, data);
export const postConfirmOrder = async (_token, data) =>
  clients['portal'].post(API.ORDER_CONFIRM, data);
export const getOrderInformation = async (_token, { reservation, shortcode }) =>
  clients['portal'].get(API.ORDER_INFORMATION, {
    params: {
      reservation,
      shortcode,
    },
  });

export const getOrderDocument = async (_token, orderId, options) =>
  clients.portal
    .get(`${API.ORDER_DOCUMENTS}/${orderId}-ORDER-CONFIRMATION-DOCUMENT`, {
      ...options,
    })
    .then(({ data }) => data)
    .catch(e => {
      // eslint-disable-next-line no-console
      console.error(e);
      throw e;
    });

export const anonReservation = async data =>
  await axios.put(API.ANON_RESERVE, data);

export const postPaymentIntent = async data =>
  await axios.post(API.STRIPE_INTENT, data);

export const getStripePublicKeys = async () => await axios.get(API.STRIPE_KEYS);

export const getConfig = async (market, channel) =>
  await axios.get(API.PAYMENT_CONFIG, {
    params: { market, channel },
  });
